import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '@/client/api';
import { Page, PageHeader, SectionTitle } from '@/client/components/AppShell';
import { AssignmentEditor, AssignmentRow } from '@/client/components/assignments';
import { SparkIcon } from '@/client/components/icons';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  SegmentedControl,
  Sheet,
  SkeletonList,
  Toggle,
} from '@/client/components/ui';
import { useWorkspace, WORKSPACE_KEY } from '@/client/store';
import { addWeeks, compareDates, formatShort } from '@/domain/dates';
import type { GenerationPreview } from '@/domain/generate';
import type { Assignment, Workspace } from '@/shared/types';

type Filter = 'upcoming' | 'gaps' | 'unconfirmed' | 'all';

/** The full schedule, plus the Generate flow. */
export function Schedule() {
  const { data: workspace, isPending } = useWorkspace();
  const [filter, setFilter] = useState<Filter>('upcoming');
  const [editing, setEditing] = useState<Assignment | null>(null);
  const [generating, setGenerating] = useState(false);

  const groups = useMemo(() => {
    if (!workspace) return [];
    return groupByMonth(filterAssignments(workspace, filter));
  }, [workspace, filter]);

  const counts = useMemo(() => {
    if (!workspace) return { upcoming: 0, gaps: 0, unconfirmed: 0, all: 0 };
    return {
      upcoming: filterAssignments(workspace, 'upcoming').length,
      gaps: filterAssignments(workspace, 'gaps').length,
      unconfirmed: filterAssignments(workspace, 'unconfirmed').length,
      all: workspace.assignments.length,
    };
  }, [workspace]);

  return (
    <>
      <PageHeader
        title="Schedule"
        actions={
          <Button variant="primary" onClick={() => setGenerating(true)} disabled={!workspace}>
            <SparkIcon className="size-4" />
            Generate
          </Button>
        }
      />

      <Page>
        <SegmentedControl<Filter>
          ariaLabel="Filter the schedule"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'upcoming', label: 'Upcoming', count: counts.upcoming },
            { value: 'gaps', label: 'Gaps', count: counts.gaps },
            { value: 'unconfirmed', label: 'To confirm', count: counts.unconfirmed },
            { value: 'all', label: 'All', count: counts.all },
          ]}
        />

        {isPending || !workspace ? (
          <SkeletonList rows={6} />
        ) : groups.length === 0 ? (
          <Card>
            <EmptyState
              title={filter === 'gaps' ? 'No gaps — every date has a name' : 'Nothing here yet'}
              description={
                filter === 'gaps'
                  ? undefined
                  : 'Run Generate to lay out the next few months from the fair rotation.'
              }
              action={
                filter === 'gaps' ? undefined : (
                  <Button variant="primary" onClick={() => setGenerating(true)}>
                    Generate schedule
                  </Button>
                )
              }
            />
          </Card>
        ) : (
          groups.map((group) => (
            <section key={group.label} className="flex flex-col gap-2">
              <SectionTitle title={group.label} />
              <ul className="flex flex-col gap-2">
                {group.items.map((assignment) => (
                  <AssignmentRow
                    key={`${assignment.date}|${assignment.riderId}`}
                    assignment={assignment}
                    workspace={workspace}
                    onOpen={setEditing}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </Page>

      {workspace ? (
        <>
          <AssignmentEditor
            assignment={editing}
            workspace={workspace}
            onClose={() => setEditing(null)}
          />
          <GenerateSheet
            open={generating}
            workspace={workspace}
            onClose={() => setGenerating(false)}
          />
        </>
      ) : null}
    </>
  );
}

function filterAssignments(workspace: Workspace, filter: Filter): Assignment[] {
  const sorted = [...workspace.assignments].sort((a, b) => compareDates(a.date, b.date));

  switch (filter) {
    case 'upcoming':
      return sorted.filter((a) => compareDates(a.date, workspace.today) >= 0);
    case 'gaps':
      return sorted.filter(
        (a) =>
          !a.volunteerId && a.outcome === 'Pending' && compareDates(a.date, workspace.today) >= 0,
      );
    case 'unconfirmed':
      return sorted.filter(
        (a) =>
          Boolean(a.volunteerId) &&
          !a.confirmed &&
          a.outcome === 'Pending' &&
          compareDates(a.date, workspace.today) >= 0,
      );
    case 'all':
      return sorted;
  }
}

function groupByMonth(assignments: Assignment[]): { label: string; items: Assignment[] }[] {
  const groups: { label: string; items: Assignment[] }[] = [];

  for (const assignment of assignments) {
    const label = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${assignment.date}T00:00:00Z`));

    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(assignment);
    else groups.push({ label, items: [assignment] });
  }

  return groups;
}

/**
 * Generate is a two-step flow: preview the proposed changes, then apply them.
 * Nothing is written until the coordinator has actually seen what will change.
 */
function GenerateSheet({
  open,
  workspace,
  onClose,
}: {
  open: boolean;
  workspace: Workspace;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const defaults = defaultRange(workspace);

  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const [includeManual, setIncludeManual] = useState(false);
  const [result, setResult] = useState<{ preview: GenerationPreview; revision: string } | null>(
    null,
  );

  const preview = useMutation({
    mutationFn: () => api.previewGeneration({ start, end, includeManual }),
    onSuccess: setResult,
  });

  const apply = useMutation({
    mutationFn: () =>
      api.applyGeneration({ start, end, includeManual, revision: result!.revision }),
    onSuccess: (data) => {
      queryClient.setQueryData(WORKSPACE_KEY, data.workspace);
      setResult(null);
      onClose();
    },
  });

  const close = () => {
    setResult(null);
    preview.reset();
    apply.reset();
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={close}
      title="Generate schedule"
      footer={
        result ? (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setResult(null)} className="flex-1">
              Back
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              busy={apply.isPending}
              disabled={result.preview.changes.length === 0}
              onClick={() => apply.mutate()}
            >
              Apply {result.preview.changes.length}
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            fullWidth
            busy={preview.isPending}
            onClick={() => preview.mutate()}
          >
            Preview changes
          </Button>
        )
      }
    >
      {result ? (
        <PreviewResult preview={result.preview} workspace={workspace} error={apply.error} />
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-ink-muted">
            Only dates in this range are touched. Confirmed rides are never changed, and hand-edited
            rows are skipped unless you say otherwise.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field label="From">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={start}
                  onChange={(event) => setStart(event.target.value)}
                />
              )}
            </Field>
            <Field label="To">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={end}
                  onChange={(event) => setEnd(event.target.value)}
                />
              )}
            </Field>
          </div>

          <Toggle
            label="Include hand-edited rows"
            description="Off by default, so your manual picks survive a regenerate."
            checked={includeManual}
            onChange={setIncludeManual}
          />

          {preview.isError ? <ErrorNotice error={preview.error} /> : null}
        </div>
      )}
    </Sheet>
  );
}

function PreviewResult({
  preview,
  workspace,
  error,
}: {
  preview: GenerationPreview;
  workspace: Workspace;
  error: unknown;
}) {
  const nameOf = (id: string | null) =>
    id ? (workspace.volunteers.find((v) => v.id === id)?.name ?? id) : 'nobody';

  const skippedByReason = preview.skipped.reduce<Record<string, number>>((acc, row) => {
    acc[row.reason] = (acc[row.reason] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-4">
      {error ? <ErrorNotice error={error} /> : null}

      <div className="flex flex-wrap gap-2">
        <Badge tone="brand">{preview.changes.length} changes</Badge>
        {preview.unfilled.length > 0 ? (
          <Badge tone="critical">{preview.unfilled.length} unfilled</Badge>
        ) : null}
        {Object.entries(skippedByReason).map(([reason, count]) => (
          <Badge key={reason}>
            {count} skipped ({reason})
          </Badge>
        ))}
      </div>

      {preview.changes.length === 0 ? (
        <EmptyState
          title="Nothing to change"
          description="Every date in this range is already settled, confirmed, or hand-edited."
        />
      ) : (
        <ul className="flex flex-col gap-1.5">
          {preview.changes.map((change) => (
            <li
              key={`${change.date}|${change.riderId}`}
              className="flex items-center justify-between gap-3 rounded-xl bg-surface-sunken px-3 py-2 text-sm"
            >
              <span className="shrink-0 text-xs font-medium text-ink-faint">
                {formatShort(change.date)}
              </span>
              <span className="min-w-0 flex-1 truncate text-right">
                {change.fromVolunteerId ? (
                  <>
                    <span className="text-ink-faint line-through">
                      {nameOf(change.fromVolunteerId)}
                    </span>
                    {' → '}
                  </>
                ) : null}
                <span className={change.toVolunteerId ? 'font-medium' : 'text-critical'}>
                  {nameOf(change.toVolunteerId)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {preview.unfilled.length > 0 ? (
        <p className="rounded-xl bg-critical-soft px-3 py-2 text-xs">
          No eligible volunteer for{' '}
          {preview.unfilled.map((row) => formatShort(row.date)).join(', ')}. Those dates stay
          unassigned.
        </p>
      ) : null}
    </div>
  );
}

function defaultRange(workspace: Workspace): { start: string; end: string } {
  const firstGap = workspace.assignments
    .filter(
      (a) =>
        !a.volunteerId && a.outcome === 'Pending' && compareDates(a.date, workspace.today) >= 0,
    )
    .map((a) => a.date)
    .sort(compareDates)[0];

  return {
    start: firstGap ?? workspace.today,
    end: addWeeks(workspace.today, workspace.settings.coordinatorHorizonWeeks),
  };
}
